import { format } from 'date-fns';

import { getCountryCurrencyConfig } from '@/constants/countries';

export type Countries = 'MEX' | 'COL' | 'CHL' | 'PER';

interface ICountryPrefix {
  country: Countries;
  phone: string;
}

export const handleCountryPrefix = ({ country, phone }: ICountryPrefix) => {
  if (!country || !phone) {
    return phone;
  }

  const countryPrefixes = {
    MEX: '52',
    COL: '57',
    CHL: '56',
    PER: '51',
  };

  const countryPhoneLengths = {
    MEX: 10,
    COL: 10,
    CHL: 9,
    PER: 9,
  };

  const prefix = countryPrefixes[country];
  const expectedLength = countryPhoneLengths[country];

  if (prefix) {
    let sanitizedPhone = phone.includes(`+${prefix}`) ? phone.replace(`+${prefix}`, '') : phone;

    // Remove all + additional characters
    sanitizedPhone = sanitizedPhone.replace(/\+/g, '');
    // Remove all white spaces
    sanitizedPhone = sanitizedPhone.replace(/\s/g, '');
    // Remove all - characters
    sanitizedPhone = sanitizedPhone.replace(/-/g, '');

    if (sanitizedPhone.length > expectedLength) {
      return `+${prefix}`;
    }

    if (sanitizedPhone.length < expectedLength) {
      return `+${prefix}${sanitizedPhone}`;
    }

    return `+${prefix}${sanitizedPhone}`;
  }
  return phone;
};

export const handleTranslations = (country: Countries) => {
  switch (country) {
    case 'MEX':
      return 'es-MEX';
    case 'COL':
      return 'es-COL';
    case 'CHL':
      return 'es-CHL';
    case 'PER':
      return 'es-PER';
    default:
      return 'es-MEX';
  }
};

export const getEtaDays = (eta: number) => {
  switch (eta) {
    case 0:
      return 'Mismo día';
    case 1:
      return '1 día';
    case null:
      return '5 a 10 días';
    default:
      return `${eta} días`;
  }
};

export const formatDate = (isoDate: string, dateFormat?: string): string => {
  // Valida que la fecha no esté vacía o nula
  if (!isoDate || isoDate.trim() === '') {
    return '--';
  }

  // Crea una instancia de Date desde la fecha ISO
  const date = new Date(isoDate);

  // Valida que la fecha sea válida
  if (isNaN(date.getTime())) {
    return '--';
  }

  // Formatea la fecha en el formato solicitado
  return format(date, dateFormat ?? 'MM/dd/yyyy');
};

type DeliveryTypes = 'SMD' | 'NXD' | 'SPT' | '99M' | 'NAL' | 'CO2F' | 'RET' | 'ECO' | 'TLM' | 'XBD';
export const formatDeliveryTypeCode = (deliveryType: DeliveryTypes): string => {
  const deliveryTypes = {
    SMD: 'Same Day',
    NXD: 'Sprint',
    SPT: 'Sprint',
    '99M': '99minutos',
    NAL: 'Nacional',
    CO2F: 'CO2 Free',
    RET: 'Retorno',
    ECO: 'Economy',
    TLM: 'Tailor Made',
    XBD: 'Crossborder',
  };

  return deliveryTypes[deliveryType] || '';
};

export const getPDF = (base64String: string) => {
  const byteCharacters = atob(base64String);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  // Crear un Blob con los datos decodificados
  const blob = new Blob([byteArray], { type: 'application/pdf' });

  // Crear una URL para el Blob
  const blobUrl = URL.createObjectURL(blob);

  // abrir la etiqueta en una ventana
  window.open(blobUrl, '_blank');
};

/**
 * Dispara la descarga de un Blob binario (xlsx, csv, etc.) en el navegador
 * usando un enlace temporal, en vez de abrir el contenido en una nueva pestaña.
 */
export const downloadBlobFile = (blob: Blob, filename: string) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

export const updateReducerState = <T>(
  dispatch: React.Dispatch<{ type: string; payload: Partial<T> }>,
  state: T,
  updates: Partial<T> | ((prevState: T) => Partial<T>),
) => {
  dispatch({
    type: 'SET_STATE',
    payload: typeof updates === 'function' ? updates(state) : updates,
  });
};

export const delay = async (ms: number) => await new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Formatea un valor numérico como moneda según el país
 * @param value - Valor numérico a formatear
 * @param country - Código del país
 * @returns Valor formateado como moneda
 */
export const formatCurrency = (value: number | null | undefined, country?: Countries): string => {
  const numValue = Number(value);
  if (value === null || value === undefined || !Number.isFinite(numValue)) return '--';

  const config = getCountryCurrencyConfig(country);

  const formatter = new Intl.NumberFormat(config.locale, {
    currency: config.currencyCode,
    currencyDisplay: 'symbol',
    currencySign: 'standard',
    style: 'currency',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(value);
};
